import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import usePiwik from 'src/hooks/usePiwik'
import { getConfig } from 'src/features/config/ConfigSelector'
import { formatCamelCaseToWords } from 'src/utils'

/**
 * Styled Components
 */
const Container = styled(Box)(({ theme }) => ({
  overflowY: 'auto'
}))

const Card = styled(Box)(() => ({
  display: 'flex-start',
  flexDirection: 'column'
}))

const ReportInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px'
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.relatedReports.title,
  fontWeight: 400,
  lineHeight: '20px',
  paddingBottom: '8px'
}))

const Type = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  fontWeight: 400,
  lineHeight: '19.5px'
}))

const ArticleContainer = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  padding: '8px'
}))

/**
 * RelatedReports Component
 */
const RelatedReports = ({ articles }) => {
  const refs = useRef({})
  const { handleActionTracking } = usePiwik()
  const { t } = useTranslation()
  const config = useSelector(getConfig('external_sites'))
  const reportsUrl = config?.Reports?.url || 'https://firstwordreports.com'

  useEffect(() => {
    const observers = []

    if (articles?.length > 0) {
      articles.forEach(article => {
        const el = refs.current[article.id]
        if (!el) return

        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            handleActionTracking('Site/View/Answer/RelatedReports', {
              articleId: article.id
            })
            observer.disconnect()
          }
        })

        observer.observe(el)
        observers.push(observer)
      })
      return () => observers.forEach(observer => observer.disconnect())
    }
  }, [articles, handleActionTracking])

  const handleRelatedReportClick = (reportId, url) => {
    handleActionTracking('Site/Click/Answer/RelatedReports', {
      articleId: reportId
    })
    window.open(url, '_blank')
  }

  return (
    <Container data-testid="related-reports">
      <Typography
        variant="h1"
        textAlign="center"
        fontWeight={700}
        fontSize="16px"
        mb="5px"
        color={theme => theme.palette.relatedReports.title}
        lineHeight="18.67px"
      >
        {t('relatedReports')}
      </Typography>
      <ArticleContainer>
        {articles?.map(article => {
          const id = article.id
          const title = article.title || 'Untitled'
          const url = `${reportsUrl}/story/${id}`
          const type =
            article?.type === 'TherapyTrends'
              ? formatCamelCaseToWords(article?.type)
              : article?.type

          return (
            <Card
              key={id}
              ref={el => (refs.current[id] = el)}
              onClick={() => handleRelatedReportClick(id, url)}
              sx={{ cursor: 'pointer' }}
            >
              <ReportInfo>
                <Title>{title}</Title>
              </ReportInfo>
              <Type>
                <Chip sx={{ padding: '0px 8px' }} size="small" label={type} />
              </Type>
            </Card>
          )
        })}
      </ArticleContainer>
    </Container>
  )
}

RelatedReports.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      type: PropTypes.string,
      body: PropTypes.string,
      extra_fields: PropTypes.object
    })
  )
}

export default RelatedReports
